import { parseAndFillContact, parseAddressErrors, hasSameAddress } from './apple-pay-helpers'

const DEBUG_MODE = false

function log(message) {
  DEBUG_MODE && window.console && window.console.log(message)
}

/*
  Pay with ApplePay will just show sum to pay with shipping and taxes:
    - user can only select with which card will pay

  Pay with ApplePay Express, in addition to above, will do a whole
  checkout flow:
    - shipping method can be selected
    - shipp and bill addresses can be selected
    - contact information (email, phone) can be selected
    - taxes will be calculated and updated on summary
*/
class ApplePay {
  constructor({ request, apiService, expressCheckout = false, onDone = () => {} }) {
    this.request = request
    this.apiService = apiService
    this.expressCheckout = expressCheckout
    this.orderChanged = false
    this.onDone = onDone

    if (expressCheckout && request.shippingMethods) {
      const firstShipMethod = request.shippingMethods[0]
      if (firstShipMethod) {
        this.selectedShipMethodIdentifier = firstShipMethod.identifier
      }
    }
  }

  beginSession() {
    return this.newSession().begin()
  }

  newSession() {
    const session = new window.ApplePaySession(3, this.request)

    session.onvalidatemerchant = event => {
      const startSession = () => {
        this.fetchOrderRequest().then(() => {
          this.callStartSession(session, event.validationURL)
        })
      }
      const waitTime = 3000
      // Attention:
      //   We wait a few seconds to let the state of the mini-cart being
      //   propagated to server. This way the first time we fetch the
      //   order items, information is accurate.
      setTimeout(startSession, waitTime)
    }
    session.onpaymentauthorized = event => {
      this.callOnPaymentAuthorized(session, event.payment)
    }
    session.onshippingmethodselected = event => {
      this.callOnShippingMethodSelected(session, event.shippingMethod)
    }
    session.onshippingcontactselected = event => {
      this.callOnShippingContactSelected(session, event.shippingContact)
    }
    session.oncancel = () => {
      this.resetCurrentOrder()
    }

    return session
  }

  /*
    ApplePay payment session created:
      - responsible for verifying that our servers can use ApplePay
      - our Apple Merchant Identifier certificate gets verified by Apple

    Apple Merchant Identifier certificates expire and need to be renewed.

    Steps from Adyen for renewing certificate:
      https://docs.adyen.com/developers/payment-methods/apple-pay/enable-apple-pay/create-a-certificate-for-your-apple-pay-merchant-identity
  */
  callStartSession(session, validationURL) {
    this.apiService
      .authorizeMerchant(validationURL)
      .then(({ data: merchantSession }) => {
        try {
          session.completeMerchantValidation(merchantSession)
        } catch (error) {
          this.abortSession(session, error)
        }
      })
      .catch(error => {
        this.abortSession(session, error)
      })
  }

  callOnPaymentAuthorized(session, payment) {
    try {
      if (this.expressCheckout) {
        this.payWithApplePayExpress(session, payment)
      } else {
        this.payWithApplePay(session, payment)
      }
    } catch (error) {
      this.abortSession(session, error)
    }
  }

  payWithApplePayExpress(session, payment) {
    const { billingContact, shippingContact } = payment

    log(
      JSON.stringify({
        billingContact,
        shippingContact,
        selectedShipping: this.selectedShipMethodIdentifier,
      })
    )

    this.updateCurrentOrder(session, {
      shippingContact,
      billingContact,
      onSuccess: () => {
        this.payWithApplePay(session, payment)
      },
      onFailure: errors => {
        this.cancelPayment(session, errors)
      },
    })
  }

  cancelPayment(session, errors) {
    this.resetCurrentOrder()
    session.completePayment({
      errors: errors || [],
      status: window.ApplePaySession.STATUS_FAILURE,
    })
  }

  payWithApplePay(session, payment) {
    const paymentToken = JSON.stringify(payment.token.paymentData)
    const paymentData = {
      'adyen-payment-token': paymentToken,
      'adyencse-terms-accepted': true,
      'adyencse-apple-pay-accepted': true,
    }

    this.apiService
      .authorizePayment(paymentData)
      .then(({ data: { success, redirect, message } }) => {
        log({ success, redirect, message })
        if (!success) {
          session.completePayment({
            status: window.ApplePaySession.STATUS_FAILURE,
          })
        } else {
          this.expressCheckout
            ? On.Analytics.trackApplePay.expressCheckoutPayment()
            : On.Analytics.trackApplePay.checkoutPayment()

          session.completePayment({
            status: window.ApplePaySession.STATUS_SUCCESS,
          })
        }

        setTimeout(() => {
          if (redirect) {
            window.location.href = redirect
          }
          this.onDone()
        }, 200)
      })
      .catch(error => this.abortSession(session, error))
  }

  callOnShippingMethodSelected(session, newShippingMethod) {
    log({ action: 'callOnShippingMethodSelected', newShippingMethod })
    const onDone = ({ status }) => {
      this.fetchOrderRequest().then(request => {
        session.completeShippingMethodSelection({
          newTotal: request.total,
          newLineItems: request.lineItems,
          newShippingMethods: request.newShippingMethods,
          status,
        })
      })
    }
    const selectedShippingMethod = this.getSelectedShippingMethod(
      this.request,
      newShippingMethod.identifier
    )

    this.selectedShipMethodIdentifier = selectedShippingMethod.identifier

    this.updateCurrentOrderDelivery(session, {
      selectedShippingMethod,
      onSuccess: () => {
        onDone({ status: window.ApplePaySession.STATUS_SUCCESS })
      },
      onFailure: () => {
        onDone({ status: window.ApplePaySession.STATUS_FAILURE })
      },
    })
  }

  callOnShippingContactSelected(session, shippingContact) {
    log({ action: 'callOnShippingContactSelected', shippingContact })
    const onDone = ({ status, errors }) => {
      this.fetchOrderRequest().then(newRequest => {
        session.completeShippingContactSelection({
          newTotal: newRequest.total,
          newLineItems: newRequest.lineItems,
          newShippingMethods: newRequest.shippingMethods,
          status,
          errors: errors || [],
        })
      })
    }

    this.selectFirstShippingMethod()
    this.updateCurrentOrder(session, {
      shippingContact,
      onSuccess: () => {
        onDone({ status: window.ApplePaySession.STATUS_SUCCESS })
      },
      onFailure: errors => {
        onDone({
          status: window.ApplePaySession.STATUS_FAILURE,
          errors,
        })
      },
    })
  }

  selectFirstShippingMethod() {
    const firstShipMethod = this.request.shippingMethods[0]
    if (firstShipMethod) {
      this.selectedShipMethodIdentifier = firstShipMethod.identifier
    }
  }

  updateCurrentOrder(session, { shippingContact, billingContact, onSuccess, onFailure }) {
    // 1. First step: checkout as guest
    const registrationAttrs = this.getOrderRegistrationAttributes({
      shippingContact,
    })
    log({ action: 'updateCheckoutRegistration', registrationAttrs })
    this.apiService.updateCheckoutRegistration(registrationAttrs).then(({ data: { success } }) => {
      if (!success) {
        log('Failed to update registration step')
        onFailure()
      } else {
        log('Successfully updated registration step')
        this.orderChanged = true
        // 2. Second step: update current order addresses
        const useShipping = billingContact ? hasSameAddress(shippingContact, billingContact) : true
        const addressAttrs = this.getOrderAddressAttributes({
          shippingContact,
          billingContact,
          useShipping,
        })
        log({ action: 'updateCheckoutAddress', addressAttrs })
        this.apiService
          .updateCheckoutAddress(addressAttrs)
          .then(({ data: { success, errors } }) => {
            if (!success) {
              log({ errors })
              log('Failed to update shippingContact and billingContact')
              const parsedErrors = parseAddressErrors(errors, {
                shippingOnly: useShipping,
              })
              onFailure(parsedErrors)
            } else {
              this.orderChanged = true
              log('Successfully updated shippingContact and billingContact')
              // 3. Third step: update delivery
              this.fetchOrderRequest().then(newRequest => {
                const selectedShippingMethod = this.getSelectedShippingMethod(
                  newRequest,
                  this.selectedShipMethodIdentifier
                )
                if (!selectedShippingMethod) {
                  log({ message: 'No shipping method available' })
                  onFailure()
                  return
                }
                log({
                  message: 'Found shipping method',
                  selectedShippingMethod,
                  shippingMethods: newRequest.shippingMethods,
                })
                this.updateCurrentOrderDelivery(session, {
                  selectedShippingMethod,
                  onSuccess,
                  onFailure,
                })
              })
            }
          })
      }
    })
  }

  getSelectedShippingMethod(request, shippingIdentifier) {
    const selectedShippingMethod = request.shippingMethods.find(
      shipMethod => shipMethod.identifier === shippingIdentifier
    )
    const firstShippingMethod = request.shippingMethods[0]
    return selectedShippingMethod || firstShippingMethod
  }

  updateCurrentOrderDelivery(session, { selectedShippingMethod, onSuccess, onFailure }) {
    const deliveryAttrs = this.getOrderDeliveryAttributes(selectedShippingMethod)
    log({ action: 'updateCheckoutDelivery', deliveryAttrs })
    this.apiService.updateCheckoutDelivery(deliveryAttrs).then(({ data: { success } }) => {
      if (success) {
        log('Successfully updated delivery')
        this.orderChanged = true
        onSuccess()
      } else {
        log('Failed to update delivery')
        onFailure()
      }
    })
  }

  getOrderRegistrationAttributes({ shippingContact }) {
    const order = {
      email: shippingContact.emailAddress || 'applepay.checkout@on-running.com',
      guest_checkout: true,
    }
    return {
      order,
      state: 'address',
      checkout: {
        order,
      },
      newsletter_subscription: false,
    }
  }

  getOrderAddressAttributes({ shippingContact, billingContact, useShipping }) {
    const orderDefaults = {
      mobile_notification: false,
      use_shipping: useShipping,
      account_ship_address_id: 0,
      account_bill_address_id: 0,
      express_checkout: true,
    }
    const shipAddress = parseAndFillContact(shippingContact)
    const billAddress = parseAndFillContact(billingContact || shippingContact)
    return {
      order: {
        ...orderDefaults,
        ship_address_attributes: shipAddress,
        bill_address_attributes: billAddress,
      },
    }
  }

  getOrderDeliveryAttributes(shippingMethod) {
    const { shipmentId, shippingRateId } = shippingMethod
    return {
      order: {
        express_checkout: true,
        shipments_attributes: [
          {
            selected_shipping_rate_id: shippingRateId,
            id: shipmentId,
          },
        ],
      },
    }
  }

  fetchOrderRequest() {
    if (!this.expressCheckout) {
      return new Promise(resolve => {
        resolve(this.request)
      })
    } else {
      return this.apiService.fetchApplePayExpressCheckoutRequest().then(request => {
        this.request = request
        return request
      })
    }
  }

  abortSession(session, error) {
    if (error) log(`Got session error: ${error.message}`)
    if (session !== null) return
    this.resetCurrentOrder()
    session.abort()
  }

  resetCurrentOrder() {
    if (this.orderChanged) {
      log('Reset current order...')
      this.apiService.resetCurrentOrder().then(data => {
        log(data)
      })
    } else {
      log('Order did not change')
    }
    this.onDone()
  }
}

export default ApplePay
